import { AxiosReadPayload } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-read-payload';
import { axiosReadHelper } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-read-helper';
import { AxiosResultStatus } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-result-status';

export type AvailityVobOverrides = {
  fname?: string;
  lname?: string;
  dob?: string;
  memberId?: string;
  pcode?: string;
  npi?: string;
};

export type FacilityWithNpi = {
  facilityId: number;
  npi: string;
  facilityName?: string;
};

/** Inline menu when fewer than this many NPI facilities; at this count use modal. */
export const AVAILITY_FACILITY_MENU_MAX = 10;

type FacilityListItem = {
  facilityId: number;
  facilityName?: string;
};

/** GET clients/v2/client/facility/{id} — finpass-ui only (no shared-platform change). */
async function getFacilityById(facilityId: number): Promise<AxiosResultStatus> {
  const payload: AxiosReadPayload = {
    dataId: -2,
    url: `clients/v2/client/facility/${facilityId}`,
  };
  return axiosReadHelper(payload);
}

/**
 * All facilities (list order) with a non-empty NPI from the facility API.
 */
export async function listFacilitiesWithValidNpi(
  facilities: FacilityListItem[] | undefined | null
): Promise<FacilityWithNpi[]> {
  if (!facilities?.length) {
    return [];
  }
  const withNpi: FacilityWithNpi[] = [];
  for (const facility of facilities) {
    const facilityId = Number(facility.facilityId);
    if (!Number.isInteger(facilityId) || facilityId <= 0) {
      continue;
    }
    const response = await getFacilityById(facilityId);
    if (response?.hasErrors || !response?.entity) {
      continue;
    }
    const entity = response.entity as {
      npi?: string | null;
      facilityName?: string | null;
    };
    const npi = String(entity.npi ?? '').trim();
    if (!npi) {
      continue;
    }
    const facilityName =
      (facility.facilityName || entity.facilityName || '').trim() ||
      undefined;
    withNpi.push({ facilityId, npi, facilityName });
  }
  return withNpi;
}

/**
 * First facility (list order) with a non-empty NPI from the facility API.
 * Used only by Patient-tab Run Availity.
 */
export async function findFirstFacilityWithValidNpi(
  facilities: FacilityListItem[] | undefined | null
): Promise<FacilityWithNpi | null> {
  const list = await listFacilitiesWithValidNpi(facilities);
  return list[0] ?? null;
}

/** GET availity/vob with optional demographics / NPI query overrides. */
export async function getAvailityVobWithOverrides(
  patientId: number,
  facilityId: number,
  overrides?: AvailityVobOverrides,
  signal?: AbortSignal
): Promise<AxiosResultStatus> {
  const params = new URLSearchParams({
    patientId: String(patientId),
    facilityId: String(facilityId),
  });
  if (overrides) {
    for (const key of ['fname', 'lname', 'dob', 'memberId', 'pcode', 'npi'] as const) {
      const value = overrides[key]?.trim();
      if (value) {
        params.set(key, value);
      }
    }
  }
  const payload: AxiosReadPayload = {
    dataId: -2,
    url: `availity/vob?${params.toString()}`,
    ...(signal ? { signal } : {}),
  };
  return axiosReadHelper(payload);
}

/** True when the Availity helper result is an aborted/canceled request. */
export function isAvailityRequestCanceled(
  response: AxiosResultStatus | null | undefined,
  signal?: AbortSignal
): boolean {
  if (signal?.aborted) {
    return true;
  }
  const message = String(response?.errorMessage ?? '').toLowerCase();
  return (
    message.includes('canceled') ||
    message.includes('cancelled') ||
    message.includes('aborted')
  );
}

export function formatAvailityDob(raw: string | null | undefined): string {
  if (!raw) {
    return '';
  }
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function facilityDisplayName(facility: FacilityWithNpi): string {
  return (facility.facilityName || '').trim() || `Facility ${facility.facilityId}`;
}
